import { z } from 'zod';

import { MatchingRecord } from '@eluve/utils';

import { AppointmentModeTypesLookup } from './appointment-mode-types';
import { AppointmentStatusTypesLookup } from './appointment-status-types';
import { AssetDeletionJobTypesLookup } from './asset-deletion-job-types';
import { FeatureFlagLookup } from './feature-flag-types';
import { FeedbackTypeLookup } from './feedback-types';
import { MedicalCodeTypesLookup } from './medical-code-types';
import { PlatformTypesLookup } from './platform-types';
import {
  TranscriptCancellationReasonLookup,
  TranscriptStatusLookup,
  TranscriptTypesLookup,
} from './transcript-types';
import {
  AppointmentFileTypesEnum,
  ExperimentModeTypesEnum,
  LlmModelTypesEnum,
  LlmOutputFailureTypesEnum,
  LlmOutputTypesEnum,
  OrganizationInviteStatusTypesEnum,
  OutgoingEmailEventTypesEnum,
  OutgoingEmailProviderTypesEnum,
  OutgoingEmailTemplateTypesEnum,
  SiteBannerTypesEnum,
  TenantUserLifecycleStatusTypesEnum,
  TranscriptionSourceEnum,
  WorkerStatusTypesEnum,
} from './types';
import { UserUploadStatusLookup } from './user-upload-status-types';
import { VendorTypesLookup } from './vendor-types';

/**
 * Provides a lookup object for the LlmOutputTypesEnum that can also
 * be used as a zod enum for runtime validation
 */
export const LlmOutputTypesLookup = {
  SOAP_NOTE: 'SOAP_NOTE', // At the moment, SOAP_NOTE represents any medical chart.
  CLIENT_RECAP: 'CLIENT_RECAP',
  FACT_VERIFICATION: 'FACT_VERIFICATION',
  BILLING_CODE_RECOMMENDATION: 'BILLING_CODE_RECOMMENDATION',
  CHAT: 'CHAT',
  DIFFERENTIAL_DIAGNOSES: 'DIFFERENTIAL_DIAGNOSES',
  GENERIC_OUTPUT: 'GENERIC_OUTPUT',
  REVIEW_OF_SYSTEMS: 'REVIEW_OF_SYSTEMS',
  PFSH: 'PFSH',
  FACT_GENERATION: 'FACT_GENERATION',
  CHIEF_COMPLAINT: 'CHIEF_COMPLAINT',
  PII_REMOVAL: 'PII_REMOVAL',
  DYNAMIC_OUTPUT: 'DYNAMIC_OUTPUT',
  TRANSLATION: 'TRANSLATION',
  MEDICINE_RECOMMENDATION: 'MEDICINE_RECOMMENDATION',
  PATIENT_OVERVIEW: 'PATIENT_OVERVIEW',
  DIFFERENTIAL_DIAGNOSES_PLUS_RED_FLAGS:
    'DIFFERENTIAL_DIAGNOSES_PLUS_RED_FLAGS',
  DIAGNOSES_JUSTIFICATION: 'DIAGNOSES_JUSTIFICATION',
  TRANSCRIPTION: 'TRANSCRIPTION',
  PREVISIT_INSIGHTS: 'PREVISIT_INSIGHTS',
  TASK_EXTRACTION: 'TASK_EXTRACTION',
} as const satisfies MatchingRecord<LlmOutputTypesEnum>;

export const MedicalNotesLlmOutputTypes = [
  LlmOutputTypesLookup.DYNAMIC_OUTPUT,
  LlmOutputTypesLookup.SOAP_NOTE,
];

export const isLlmOutputMedicalNote = (
  outputType: LlmOutputTypesEnum | string = '',
) =>
  MedicalNotesLlmOutputTypes.map((type) => type.toString()).includes(
    outputType?.toString(),
  );

export const isLlmOutputDynamicOutput = (
  outputType?: LlmOutputTypesEnum | string | null,
) => LlmOutputTypesLookup.DYNAMIC_OUTPUT.toString() === outputType?.toString();

export const llmOutputTypesSchema = z.nativeEnum(LlmOutputTypesLookup);

export const LlmModelTypesLookup = {
  CLAUDE_2: 'CLAUDE_2',
  CLAUDE_3: 'CLAUDE_3',
  CLAUDE_4_5: 'CLAUDE_4_5',
  CLAUDE_4_6: 'CLAUDE_4_6',
  // temperature/top_p/top_k return a 400 on this line — rows must omit them.
  CLAUDE_5: 'CLAUDE_5',
  GPT_4: 'GPT_4',
  LLAMA_3: 'LLAMA_3',
  GPT_4_O: 'GPT_4_O',
  GPT_5: 'GPT_5',
  DIFY_SERVICE: 'DIFY_SERVICE',
  GEMINI: 'GEMINI',
  GROQ: 'GROQ',
  LIGHTNING_AI: 'LIGHTNING_AI',
} as const satisfies MatchingRecord<LlmModelTypesEnum>;

export const llmModelTypesSchema = z.nativeEnum(LlmModelTypesLookup);

export type LlmModelType = z.infer<typeof llmModelTypesSchema>;

export type PatientArtifactTypesEnum =
  | 'PATIENT_OVERVIEW'
  | 'LATEST_MEDICAL_NOTE'
  | 'LATEST_MEDICAL_NOTE_2'
  | 'LATEST_MEDICAL_NOTE_3'
  | 'LATEST_DOCTOR_NOTE'
  | 'LATEST_DOCTOR_NOTE_2'
  | 'LATEST_DOCTOR_NOTE_3'
  | 'ALL_DOCTOR_NOTES'
  | 'PATIENT_DETAILS'
  | 'ALL_MEDICAL_NOTES'
  | 'LATEST_APPOINTMENT_INTAKE_CONVERSATION'
  | 'CURRENT_APPOINTMENT_DATE'
  | 'PREVIOUS_APPOINTMENT_DATE'
  | 'PREVIOUS_APPOINTMENT_DATE_2'
  | 'PREVIOUS_APPOINTMENT_DATE_3'
  | 'DAYS_SINCE_LAST_APPOINTMENT'
  // Clinician-scoped counterparts of the variables above. Same resolver
  // pipeline, restricted to the current appointment's clinician.
  | 'DOCTOR_ALL_MEDICAL_NOTES'
  | 'DOCTOR_LATEST_MEDICAL_NOTE'
  | 'DOCTOR_LATEST_MEDICAL_NOTE_2'
  | 'DOCTOR_LATEST_MEDICAL_NOTE_3'
  | 'DOCTOR_PREVIOUS_APPOINTMENT_DATE'
  | 'DOCTOR_PREVIOUS_APPOINTMENT_DATE_2'
  | 'DOCTOR_PREVIOUS_APPOINTMENT_DATE_3'
  | 'DOCTOR_DAYS_SINCE_LAST_APPOINTMENT'
  | 'LANGUAGE';

export const PatientArtifactTypesLookup = {
  PATIENT_OVERVIEW: 'PATIENT_OVERVIEW',
  LATEST_MEDICAL_NOTE: 'LATEST_MEDICAL_NOTE',
  LATEST_MEDICAL_NOTE_2: 'LATEST_MEDICAL_NOTE_2',
  LATEST_MEDICAL_NOTE_3: 'LATEST_MEDICAL_NOTE_3',
  LATEST_DOCTOR_NOTE: 'LATEST_DOCTOR_NOTE',
  LATEST_DOCTOR_NOTE_2: 'LATEST_DOCTOR_NOTE_2',
  LATEST_DOCTOR_NOTE_3: 'LATEST_DOCTOR_NOTE_3',
  ALL_DOCTOR_NOTES: 'ALL_DOCTOR_NOTES',
  PATIENT_DETAILS: 'PATIENT_DETAILS',
  ALL_MEDICAL_NOTES: 'ALL_MEDICAL_NOTES',
  LATEST_APPOINTMENT_INTAKE_CONVERSATION:
    'LATEST_APPOINTMENT_INTAKE_CONVERSATION',
  CURRENT_APPOINTMENT_DATE: 'CURRENT_APPOINTMENT_DATE',
  PREVIOUS_APPOINTMENT_DATE: 'PREVIOUS_APPOINTMENT_DATE',
  PREVIOUS_APPOINTMENT_DATE_2: 'PREVIOUS_APPOINTMENT_DATE_2',
  PREVIOUS_APPOINTMENT_DATE_3: 'PREVIOUS_APPOINTMENT_DATE_3',
  DAYS_SINCE_LAST_APPOINTMENT: 'DAYS_SINCE_LAST_APPOINTMENT',
  DOCTOR_ALL_MEDICAL_NOTES: 'DOCTOR_ALL_MEDICAL_NOTES',
  DOCTOR_LATEST_MEDICAL_NOTE: 'DOCTOR_LATEST_MEDICAL_NOTE',
  DOCTOR_LATEST_MEDICAL_NOTE_2: 'DOCTOR_LATEST_MEDICAL_NOTE_2',
  DOCTOR_LATEST_MEDICAL_NOTE_3: 'DOCTOR_LATEST_MEDICAL_NOTE_3',
  DOCTOR_PREVIOUS_APPOINTMENT_DATE: 'DOCTOR_PREVIOUS_APPOINTMENT_DATE',
  DOCTOR_PREVIOUS_APPOINTMENT_DATE_2: 'DOCTOR_PREVIOUS_APPOINTMENT_DATE_2',
  DOCTOR_PREVIOUS_APPOINTMENT_DATE_3: 'DOCTOR_PREVIOUS_APPOINTMENT_DATE_3',
  DOCTOR_DAYS_SINCE_LAST_APPOINTMENT: 'DOCTOR_DAYS_SINCE_LAST_APPOINTMENT',
  LANGUAGE: 'LANGUAGE',
} as const satisfies MatchingRecord<PatientArtifactTypesEnum>;

export const WorkerStatusTypesLookup = {
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  FAILED_WORKER_TERMINATED: 'FAILED_WORKER_TERMINATED',
  IN_PROGRESS: 'IN_PROGRESS',
  NOT_STARTED: 'NOT_STARTED',
  PENDING_DATA_UPLOAD: 'PENDING_DATA_UPLOAD',
  CANCELLED: 'CANCELLED',
} as const satisfies MatchingRecord<WorkerStatusTypesEnum>;

export const LlmOutputFailureTypesLookup = {
  TEMPLATE_MISSING: 'TEMPLATE_MISSING',
  DEGRADED_TRANSCRIPT: 'DEGRADED_TRANSCRIPT',
  NO_MEDICAL_CONTEXT: 'NO_MEDICAL_CONTEXT',
  TRANSCRIPT_TOO_SHORT: 'TRANSCRIPT_TOO_SHORT',
  LLM_GENERATION_ERROR: 'LLM_GENERATION_ERROR',
  SCHEMA_VALIDATION_ERROR: 'SCHEMA_VALIDATION_ERROR',
  UNKNOWN: 'UNKNOWN',
} as const satisfies MatchingRecord<LlmOutputFailureTypesEnum>;

export const SiteBannerTypesLookup = {
  INFO: 'INFO',
  WARNING: 'WARNING',
  CRITICAL: 'CRITICAL',
} as const satisfies MatchingRecord<SiteBannerTypesEnum>;

export const OutgoingEmailProviderTypesLookup = {
  AWS_SES: 'AWS_SES',
} as const satisfies MatchingRecord<OutgoingEmailProviderTypesEnum>;

export const OutgoingEmailEventTypesLookup = {
  BOUNCED: 'BOUNCED',
  DELIVERED: 'DELIVERED',
  OPENED: 'OPENED',
  REJECTED: 'REJECTED',
  SENT: 'SENT',
} as const satisfies MatchingRecord<OutgoingEmailEventTypesEnum>;

export const OutgoingEmailTemplateTypesLookup = {
  SIGNIN_MAGIC_LINK: 'SIGNIN_MAGIC_LINK',
  SUPERBILL: 'SUPERBILL',
  USER_INVITE: 'USER_INVITE',
  WELCOME: 'WELCOME',
} as const satisfies MatchingRecord<OutgoingEmailTemplateTypesEnum>;

export type AppointmentGoldenNotesStatusEnum = 'DRAFT' | 'COMPLETED';

export const AppointmentGoldenNotesStatusLookup = {
  DRAFT: 'DRAFT',
  COMPLETED: 'COMPLETED',
} as const satisfies MatchingRecord<AppointmentGoldenNotesStatusEnum>;

const OrganizationInviteStatusTypesLookup = {
  PENDING: 'PENDING',
  ACCEPTED: 'ACCEPTED',
  EXPIRED: 'EXPIRED',
  AUTO_ACCEPTED: 'AUTO_ACCEPTED',
  REVOKED: 'REVOKED',
} as const satisfies MatchingRecord<OrganizationInviteStatusTypesEnum>;

const TenantUserLifecycleStatusTypesLookup = {
  CREATED: 'CREATED',
  ONBOARDED: 'ONBOARDED',
  ACTIVE: 'ACTIVE',
  CHURNED: 'CHURNED',
} as const satisfies MatchingRecord<TenantUserLifecycleStatusTypesEnum>;

const AppointmentFileTypesLookup = {
  ADMIN_UPLOAD_AUDIO: 'ADMIN_UPLOAD_AUDIO',
  CHART_PDF: 'CHART_PDF',
  CHUNKED_AUDIO: 'CHUNKED_AUDIO',
  RAW_TRANSCRIPT_METADATA: 'RAW_TRANSCRIPT_METADATA',
  SUPERBILL_PDF: 'SUPERBILL_PDF',
  TRANSCODED_AUDIO_FILE: 'TRANSCODED_AUDIO_FILE',
  USER_BACKUP_AUDIO: 'USER_BACKUP_AUDIO',
  WEBSOCKET_AUDIO: 'WEBSOCKET_AUDIO',
} as const satisfies MatchingRecord<AppointmentFileTypesEnum>;

const TranscriptionSourceLookup = {
  AZURE_AI_CONTINUOUS_LANGUAGE_IDENTIFICATION:
    'AZURE_AI_CONTINUOUS_LANGUAGE_IDENTIFICATION',
  AZURE_AI_SINGLE_LANGUAGE: 'AZURE_AI_SINGLE_LANGUAGE',
  DEEPGRAM_NOVA_2_GENERAL: 'DEEPGRAM_NOVA_2_GENERAL',
  DEEPGRAM_NOVA_2_MEDICAL: 'DEEPGRAM_NOVA_2_MEDICAL',
  DEEPGRAM_NOVA_3: 'DEEPGRAM_NOVA_3',
  DEEPGRAM_NOVA_3_MEDICAL: 'DEEPGRAM_NOVA_3_MEDICAL',
  ELEVENLABS_SCRIBE_V2: 'ELEVENLABS_SCRIBE_V2',
  ELEVENLABS_SCRIBE_V2_TURBO: 'ELEVENLABS_SCRIBE_V2_TURBO',
  GEMINI_2_0_FLASH: 'GEMINI_2_0_FLASH',
  GEMINI_2_5_FLASH: 'GEMINI_2_5_FLASH',
  GEMINI_2_5_PRO: 'GEMINI_2_5_PRO',
} as const satisfies MatchingRecord<TranscriptionSourceEnum>;

const ExperimentModeTypesLookup = {
  COMPARISON: 'COMPARISON',
  AB_TEST: 'AB_TEST',
} as const satisfies MatchingRecord<ExperimentModeTypesEnum>;

export const DbEnums = {
  UserUploadStatus: UserUploadStatusLookup,
  PatientArtifactType: PatientArtifactTypesLookup,
  LlmModelType: LlmModelTypesLookup,
  LlmOutputType: LlmOutputTypesLookup,
  LlmOutputFailureType: LlmOutputFailureTypesLookup,
  AssetDeletionJobType: AssetDeletionJobTypesLookup,
  AppointmentStatus: AppointmentStatusTypesLookup,
  FeedbackType: FeedbackTypeLookup,
  AppointmentMode: AppointmentModeTypesLookup,
  AppointmentFileType: AppointmentFileTypesLookup,
  MedicalCodeTypes: MedicalCodeTypesLookup,
  WorkerStatus: WorkerStatusTypesLookup,
  VendorTypes: VendorTypesLookup,
  SiteBannerTypes: SiteBannerTypesLookup,
  TranscriptTypes: TranscriptTypesLookup,
  TranscriptStatus: TranscriptStatusLookup,
  TranscriptCancellationReason: TranscriptCancellationReasonLookup,
  FeatureFlag: FeatureFlagLookup,
  OutgoingEmailProviderTypes: OutgoingEmailProviderTypesLookup,
  OutgoingEmailEventTypes: OutgoingEmailEventTypesLookup,
  OutgoingEmailTemplateTypes: OutgoingEmailTemplateTypesLookup,
  AppointmentGoldenNotesStatus: AppointmentGoldenNotesStatusLookup,
  OrganizationInviteStatus: OrganizationInviteStatusTypesLookup,
  TenantUserLifecycleStatus: TenantUserLifecycleStatusTypesLookup,
  TranscriptionSource: TranscriptionSourceLookup,
  PlatformType: PlatformTypesLookup,
  ExperimentModeTypes: ExperimentModeTypesLookup,
};
